import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Undo2 } from 'lucide-react';
import { returnAssetFromBranchSchema, type ReturnAssetFromBranchInput } from '@pms/shared';
import { assetsService, type AssetBranchTransfer } from '@/services/assets.service';
import { getErrorMessage } from '@/lib/errors';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface Props {
  transfer: AssetBranchTransfer | null;
  onClose: () => void;
}

export function ReturnAssetDialog({ transfer, onClose }: Props) {
  const queryClient = useQueryClient();
  // The parent keys this dialog by transfer id, so each open starts with a fresh form.
  const { register, handleSubmit, formState: { errors } } = useForm<ReturnAssetFromBranchInput>({
    resolver: zodResolver(returnAssetFromBranchSchema),
    defaultValues: { notes: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: ReturnAssetFromBranchInput) =>
      assetsService.returnFromBranch(transfer!.id, { notes: values.notes?.trim() || undefined }),
    onSuccess: () => {
      toast.success(`${transfer?.asset_code} returned to stock`);
      void queryClient.invalidateQueries({ queryKey: ['assets', 'branch-transfers'] });
      void queryClient.invalidateQueries({ queryKey: ['assets', 'list'] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const holder = transfer?.live?.state === 'assigned' ? transfer.live.user_name : null;

  return (
    <Dialog open={!!transfer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="h-5 w-5 text-primary" />
            Return from {transfer?.branch.label}
          </DialogTitle>
          <DialogDescription>
            <code className="font-mono font-semibold">{transfer?.asset_code}</code> is removed from{' '}
            {transfer?.branch.label}
            {holder && <> (it is currently assigned to <strong>{holder}</strong> there)</>} and comes back
            to this company's asset list, unassigned and In Stock.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="return-notes">Notes</Label>
            <Textarea id="return-notes" {...register('notes')} rows={2} placeholder="Optional — condition, reason" />
            {errors.notes && <p className="text-xs text-destructive">{errors.notes.message}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Return to stock
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
